import React, { ReactNode, ErrorInfo } from 'react'
import { AlertError } from 'vnetwork-ui'
import * as Sentry from '@sentry/react'

interface ErrorBoundaryProps {
    children: ReactNode
}

interface ErrorBoundaryState {
    hasError: boolean
    error: Error | null
    info: ErrorInfo | null
    stack: string | null
}

class ErrorBoundary extends React.Component<
    ErrorBoundaryProps,
    ErrorBoundaryState
> {
    constructor(props: ErrorBoundaryProps) {
        super(props)
        this.state = { hasError: false, error: null, info: null, stack: null }
    }

    static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
        return { hasError: true }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error(error, errorInfo)

        // Capture the error in Sentry with additional context
        Sentry.withScope((scope) => {
            scope.setContext('errorInfo', {
                componentStack: errorInfo.componentStack,
            })
            scope.setLevel('error')
            Sentry.captureException(error)
        })

        this.setState({ error, info: errorInfo, stack: error.stack || null })
    }

    render() {
        if (this.state.hasError) {
            return <AlertError error={this.state.error as Error} />
        }

        return this.props.children
    }
}

export default ErrorBoundary
